import type { ModuleKey } from "Infrastructure/Api/generated/types.gen";

/**
 * Reserved value placed in a run's id URL slot to denote an anonymous, FE-only
 * draft (no server-persisted row yet). Anonymous workspaces match the sentinel
 * route declared in the router; the constant is shared so the workspace pages
 * and navigation components agree on the same literal without sprinkling magic
 * strings across files. Currently used by the anonymous PathRun draft flow.
 */
export const DRAFT_RUN_SENTINEL = "draft";

export const AppRouting = {
	Error403: {
		path: () => "/403",
	},
	Error404: {
		path: () => "/404",
	},
	SignIn: {
		path: () => "/",
	},
	BrokerAuth: {
		path: () => "/auth/broker",
	},
	Dashboard: {
		path: () => "/dashboard",
	},
	NullabilityTest: {
		path: () => "/test/nullability",
	},
	// Modeling Studio
	ModuleLibrary: {
		path: () => "/modeling/modules",
	},
	// Standalone, isolated view of a single calculator (no run, no navigation to
	// sibling modules). The grouped/persisted experience lives in System paths.
	ModuleStandalone: {
		route: "/modeling/modules/run/:moduleKey",
		path: (moduleKey: ModuleKey) => `/modeling/modules/run/${moduleKey}`,
	},
	PdfBuilderNew: {
		path: () => "/modeling/modules/pdf/new",
	},
	PdfBuilderEdit: {
		route: "/modeling/modules/pdf/:customModuleId/edit",
		path: (customModuleId: number | string) =>
			`/modeling/modules/pdf/${customModuleId}/edit`,
	},
	FormBuilderNew: {
		path: () => "/modeling/modules/form/new",
	},
	FormBuilderEdit: {
		route: "/modeling/modules/form/:customModuleId/edit",
		path: (customModuleId: number | string) =>
			`/modeling/modules/form/${customModuleId}/edit`,
	},
	PathTemplateLibrary: {
		path: () => "/modeling/path-templates",
	},
	PathTemplateNew: {
		path: () => "/modeling/path-templates/new",
	},
	PathTemplateEdit: {
		route: "/modeling/path-templates/:pathTemplateId/edit",
		path: (pathTemplateId: number | string) =>
			`/modeling/path-templates/${pathTemplateId}/edit`,
	},
	PathTemplateCopy: {
		route: "/modeling/path-templates/:pathTemplateId/copy",
		path: (pathTemplateId: number | string) =>
			`/modeling/path-templates/${pathTemplateId}/copy`,
	},
	PathRunList: {
		path: () => "/modeling/paths",
	},
	PathRunDetail: {
		route: "/modeling/paths/:pathRunId",
		path: (pathRunId: number | string) => `/modeling/paths/${pathRunId}`,
	},
	PathRunStepDetail: {
		route: "/modeling/paths/:pathRunId/steps/:pathRunStepId",
		path: (pathRunId: number | string, pathRunStepId: number | string) =>
			`/modeling/paths/${pathRunId}/steps/${pathRunStepId}`,
	},
	PathRunDraftStep: {
		route: `/modeling/paths/${DRAFT_RUN_SENTINEL}/templates/:pathTemplateId/steps/:stepOrder`,
		path: (pathTemplateId: number | string, stepOrder: number) =>
			`/modeling/paths/${DRAFT_RUN_SENTINEL}/templates/${pathTemplateId}/steps/${stepOrder}`,
	},
	// Public, anonymously-readable client views resolved by an opaque share
	// token (no internal ids exposed). Live outside the authenticated layout.
	// Fixed, readable Czech path segments (klient = client view, vypocet/cesta =
	// kind) make the link friendly without leaking any data; the token is the key.
	// ⚠️ The backend builds these same URLs for share e-mails from
	// `Application/Modeling/Sharing/PublicShareRoutes.cs`. If you change a segment
	// here, change it there too — there is no compile-time link and e-mailed links
	// would otherwise 404.
	PublicPathShare: {
		route: "/klient/cesta/:shareToken",
		path: (shareToken: string) => `/klient/cesta/${shareToken}`,
	},
	PublicModuleShare: {
		route: "/klient/vypocet/:shareToken",
		path: (shareToken: string) => `/klient/vypocet/${shareToken}`,
	},
	// Print-only render target for server-side PDF generation (Gotenberg
	// navigates here). The module key + inputs JSON travel in the query string,
	// so the page renders fully self-contained without an API/token round-trip.
	PrintModule: {
		path: () => "/print/modul",
	},
	// Print-only render target for whole-path PDF generation. The render token +
	// the selected step orders travel in the query string; the page fetches the
	// run's step data via the token-gated anonymous render endpoint.
	PrintPath: {
		path: () => "/print/cesta",
	},
} as const;

type RouteObject = {
	route?: string;
	path: (...args: never[]) => string;
};

export function getRoute<T extends RouteObject>(routeObj: T): string {
	return routeObj.route ?? routeObj.path();
}

export function resolveStartPage(
	user: unknown | null,
	unauthenticatedUrl: string | null,
) {
	if (user === null) {
		return AppRouting.SignIn.path();
	}

	if (unauthenticatedUrl) {
		return unauthenticatedUrl;
	}
	return AppRouting.Dashboard.path();
}

export function getPathnameAndSearch(location: {
	pathname: string;
	search: string;
}) {
	return `${location.pathname}${location.search}`;
}

/**
 * Builds an absolute, externally-shareable URL for a public route path (the
 * result of `AppRouting.PublicXShare.path(token)`). Includes the app base path
 * so the link works regardless of where the SPA is mounted; suitable for
 * copy-to-clipboard / sending to a client.
 */
export function buildPublicShareUrl(routePath: string): string {
	const base = (import.meta.env.VITE_BASE_URL ?? "").replace(/\/$/, "");
	return `${window.location.origin}${base}${routePath}`;
}
